import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';
import {
  LoadAppTypes,
  LoadAppTypesFail,
  LoadAppTypesSuccess,
  LoadBillingServiceTypes,
  LoadBillingServiceTypesFail,
  LoadBillingServiceTypesSuccess,
  LoadVehicleTypes,
  LoadVehicleTypesFail,
  LoadVehicleTypesSuccess,
  LoadCallLogFieldsValues,
  LoadCallLogFieldsValuesFail,
  LoadCallLogFieldsValuesSuccess,
  LoadCancelReasons,
  LoadCancelReasonsFail,
  LoadCancelReasonsSuccess,
  LoadChecklists,
  LoadChecklistsFail,
  LoadChecklistsSuccess,
  LoadContractPackages,
  LoadContractPackagesFail,
  LoadContractPackagesSuccess,
  LoadCountries,
  LoadCountriesFail,
  LoadCountriesSuccess,
  LoadCustomerActionReasons,
  LoadCustomerActionReasonsFail,
  LoadCustomerActionReasonsSuccess,
  LoadHubs,
  LoadHubsFail,
  LoadHubsSuccess,
  LoadOrderCategories,
  LoadOrderCategoriesFail,
  LoadOrderCategoriesSuccess,
  LoadOrderTypes,
  LoadOrderTypesFail,
  LoadOrderTypesSuccess,
  LoadPermissions,
  LoadPermissionsFail,
  LoadPermissionsSuccess,
  LoadRouteStatuses,
  LoadRouteStatusesFail,
  LoadRouteStatusesSuccess,
  LoadStatusGroups,
  LoadStatusGroupsFail,
  LoadStatusGroupsSuccess,
  LoadStoryStatuses,
  LoadStoryStatusesFail,
  LoadStoryStatusesSuccess,
  LoadSubsidiaries,
  LoadSubsidiariesFail,
  LoadSubsidiariesSuccess,
  PrintAwbs,
  PrintAwbsFail,
  PrintAwbsSuccess,
  SearchOrder,
  StopLoadingHubs,
  LoadCancelReasonsByOrdersSuccess,
  LoadCancelReasonsByOrdersFail,
  LoadCancelReasonsByOrders,
} from '../actions/data.actions';
import { DataService } from '../../services/data.service';
import { Hub } from '../../interfaces/hub.interface';
import { State } from '../index';
import { Store } from '@ngrx/store';
import { selectAllHubs } from '../selectors/hubs.selectors';
import { selectSubsidiaries } from '../selectors/subsidiaries.selectors';
import { Subsidiary } from '@general-data/interfaces/subsidiary.interface';
import { Permission } from '@general-data/interfaces/permission.interface';
import { selectPermissions } from '../selectors/permissions.selectors';
import { selectCancelReasons } from '../selectors/cancel-reasons.selectors';
import { selectOrderTypes } from '../selectors/order-types.selectors';
import { selectOrderCategories } from '../selectors/order-categories.selectors';
import { WhatToDoService } from '@shared/services/what-to-do.service';
import { AmbigousScanResponse, GenericScanResp } from '@core/interfaces/scan-response.interface';
import { selectAllCountries } from '../selectors/countries.selectors';
import { selectCustomerActionReasons } from '../selectors/customer-action-reasons.selectors';
import { CancelReason } from '@general-data/interfaces/cancel-reason.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { selectChecklists } from '../selectors/checklist.selectors';
import { selectStoryStatuses } from '../selectors/story-statuses.selectors';
import { selectRouteStatuses } from '../selectors/route-statuses.selectors';
import { selectStatusGroups } from '../selectors/status-groups.selectors';
import { selectContractPackages } from '../selectors/contract-packages.selectors';
import { selectAppTypes } from '../selectors/app-types.selectors';
import { selectCallCommunicationTypes } from '../selectors/call-logs-fields.selectors';
import { selectBillingServiceTypes } from '../selectors/billing-service-types.selectors';
import { CancelReasonByOrder } from '@general-data/interfaces/cancel-reason-by-order.interface';
import { selectVehicleTypes } from '../selectors/vehicle-types.selectors';

@Injectable()
export class DataEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly store = inject<Store<State>>(Store);
  private readonly whatToDoService = inject(WhatToDoService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  loadHubs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadHubs),
      withLatestFrom(this.store.select(selectAllHubs)),
      // filter(([action, hubs]) => hubs === null),
      mergeMap(([_, hubs]) => {
        if (!hubs) {
          return this.dataService.getHubs().pipe(
            map((hubs: Hub[]) => LoadHubsSuccess({ hubs })),
            catchError(({ error }) => of(LoadHubsFail({ error })))
          );
        } else {
          return of(StopLoadingHubs());
        }
      })
    )
  );

  loadSubsidiaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSubsidiaries),
      withLatestFrom(this.store.select(selectSubsidiaries)),
      filter(([_action, subsidiaries]) => subsidiaries === null),
      exhaustMap(([_action, _subsidiaries]) =>
        this.dataService.getSubsidiaries().pipe(
          map((subsidiaries: Subsidiary[]) => LoadSubsidiariesSuccess({ subsidiaries })),
          catchError(({ error }) => {
            return of(LoadSubsidiariesFail({ error }));
          })
        )
      )
    )
  );

  loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPermissions),
      withLatestFrom(this.store.select(selectPermissions)),
      filter(([_action, permissions]) => permissions === null),
      exhaustMap(([_action, _permissions]) =>
        this.dataService.getPermissions().pipe(
          map((permissions: Permission[]) => LoadPermissionsSuccess({ permissions })),
          catchError(({ error }) => {
            return of(LoadPermissionsFail({ error }));
          })
        )
      )
    )
  );

  loadCancelReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCancelReasons),
      withLatestFrom(this.store.select(selectCancelReasons)),
      filter(([_action, cancelReasons]) => cancelReasons === null),
      exhaustMap(([_action, _cancelReasons]) => {
        return this.dataService.getCancelReasons().pipe(
          map((cancelReasons: CancelReason[]) => LoadCancelReasonsSuccess({ cancelReasons })),
          catchError(({ error }) => of(LoadCancelReasonsFail({ error })))
        );
      })
    )
  );

  loadCancelReasonsByOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCancelReasonsByOrders),
      exhaustMap(({ ordersIds }) =>
        this.dataService.getCancelReasonsByOrders(ordersIds).pipe(
          map((cancelReasonsByOrders: CancelReasonByOrder[]) =>
            LoadCancelReasonsByOrdersSuccess({ cancelReasonsByOrders })
          ),
          catchError(({ error }) => of(LoadCancelReasonsByOrdersFail({ error })))
        )
      )
    )
  );

  loadOrderTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadOrderTypes),
      withLatestFrom(this.store.select(selectOrderTypes)),
      filter(([_action, orderTypes]) => orderTypes === null),
      exhaustMap(([_action, _orderTypes]) => {
        return this.dataService.getOrderTypes().pipe(
          map((orderTypes: string[]) => LoadOrderTypesSuccess({ orderTypes })),
          catchError(({ error }) => of(LoadOrderTypesFail({ error })))
        );
      })
    )
  );

  loadOrderCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadOrderCategories),
      withLatestFrom(this.store.select(selectOrderCategories)),
      filter(([_action, orderCategories]) => orderCategories === null),
      exhaustMap(([_action, _orderCategories]) => {
        return this.dataService.getOrderCategories().pipe(
          map((orderCategories: string[]) => LoadOrderCategoriesSuccess({ orderCategories })),
          catchError(({ error }) => of(LoadOrderCategoriesFail({ error })))
        );
      })
    )
  );

  searchOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SearchOrder),
      exhaustMap(({ trackingNumber, ambiguousSelected }) => {
        return this.dataService.searchOrder(trackingNumber, ambiguousSelected).pipe(
          map((scanResponse) => {
            if (scanResponse.status === 'ambiguous') {
              scanResponse.refNumber = trackingNumber; // add refNumber to the response to show in the dialog
              this.whatToDoService.openAmbiguousDialog(scanResponse as AmbigousScanResponse);
            } else {
              if (scanResponse.status === 'error') {
                scanResponse.notFoundOrder = trackingNumber; // add notFoundOrder to the response to show in the dialog
              }
              // add urlParam to identify that it is from search
              this.router.navigate([], { relativeTo: this.route, queryParams: { isSearch: true } }).then(() => {
                this.whatToDoService.openWTDDialog(scanResponse as GenericScanResp);
              });
            }
            return { type: 'NO_ACTION' };
          }),
          catchError(({ error }) => {
            const response = {
              status: 'error',
              message: error.message,
            };
            this.whatToDoService.openWTDDialog(response as GenericScanResp);
            return of({ type: 'NO_ACTION' });
          })
        );
      })
    )
  );

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCountries),
      withLatestFrom(this.store.select(selectAllCountries)),
      filter(([_action, countries]) => countries === null),
      exhaustMap(() =>
        this.dataService.getCountries().pipe(
          map((countries) => LoadCountriesSuccess({ countries })),
          catchError(({ error }) => of(LoadCountriesFail({ error })))
        )
      )
    )
  );

  loadCustomerActionReasons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCustomerActionReasons),
      withLatestFrom(this.store.select(selectCustomerActionReasons)),
      filter(([_action, customerActionReasons]) => customerActionReasons === null),
      exhaustMap((_action) =>
        this.dataService.getCustomerActionReasons().pipe(
          map((customerActionReasons) => LoadCustomerActionReasonsSuccess({ customerActionReasons })),
          catchError(({ error }) => of(LoadCustomerActionReasonsFail({ error })))
        )
      )
    )
  );

  printAwbs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PrintAwbs),
      exhaustMap(({ template, copies, trackingNumbers }) =>
        this.dataService.printAwbs(template, copies, trackingNumbers).pipe(
          map((url) => PrintAwbsSuccess({ url })),
          catchError(({ error }) => of(PrintAwbsFail({ error })))
        )
      )
    )
  );

  printAwbsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PrintAwbsSuccess),
        tap(({ url }) => {
          const link = document.createElement('a');
          link.href = url;
          link.download = '';
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
      ),
    { dispatch: false }
  );

  loadChecklists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadChecklists),
      withLatestFrom(this.store.select(selectChecklists)),
      filter(([_action, checklists]) => checklists === null),
      exhaustMap(() =>
        this.dataService.getChecklists().pipe(
          map((checklists) => LoadChecklistsSuccess({ checklists })),
          catchError(({ error }) => of(LoadChecklistsFail({ error })))
        )
      )
    )
  );

  loadStoryStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadStoryStatuses),
      withLatestFrom(this.store.select(selectStoryStatuses)),
      filter(([_action, storyStatuses]) => storyStatuses === null),
      exhaustMap(([_action, _storyStatuses]) => {
        return this.dataService.getStoryStatuses().pipe(
          map((storyStatuses: string[]) => LoadStoryStatusesSuccess({ storyStatuses })),
          catchError(({ error }) => of(LoadStoryStatusesFail({ error })))
        );
      })
    )
  );

  loadRouteStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadRouteStatuses),
      withLatestFrom(this.store.select(selectRouteStatuses)),
      filter(([_action, routeStatuses]) => routeStatuses === null),
      exhaustMap(([_action, _routeStatuses]) => {
        return this.dataService.getRouteStatuses().pipe(
          map((routeStatuses: string[]) => LoadRouteStatusesSuccess({ routeStatuses })),
          catchError(({ error }) => of(LoadRouteStatusesFail({ error })))
        );
      })
    )
  );

  loadStatusGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadStatusGroups),
      withLatestFrom(this.store.select(selectStatusGroups)),
      filter(([_action, statusGroups]) => statusGroups === null),
      exhaustMap(([_action, _statusGroups]) => {
        return this.dataService.getStatusGroups().pipe(
          map((statusGroups: string[]) => LoadStatusGroupsSuccess({ statusGroups })),
          catchError(({ error }) => of(LoadStatusGroupsFail({ error })))
        );
      })
    )
  );

  loadContractPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadContractPackages),
      withLatestFrom(this.store.select(selectContractPackages)),
      filter(([_action, contractPackages]) => contractPackages === null),
      exhaustMap(() => {
        return this.dataService.getContractPackages().pipe(
          map((contractPackages: string[]) => LoadContractPackagesSuccess({ contractPackages })),
          catchError(({ error }) => of(LoadContractPackagesFail({ error })))
        );
      })
    )
  );

  loadAppTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadAppTypes),
      withLatestFrom(this.store.select(selectAppTypes)),
      filter(([_action, appTypes]) => appTypes === null),
      exhaustMap(() => {
        return this.dataService.getAppTypes().pipe(
          map((appTypes: string[]) => LoadAppTypesSuccess({ appTypes })),
          catchError(({ error }) => of(LoadAppTypesFail({ error })))
        );
      })
    )
  );

  loadCallLogFieldsValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadCallLogFieldsValues),
      withLatestFrom(this.store.select(selectCallCommunicationTypes)),
      filter(([_action, communicationTypes]) => communicationTypes === null),
      exhaustMap(() =>
        this.dataService.getCallLogFieldsValues().pipe(
          map(
            ({
              communicationTypes,
              departments,
              interactionBy,
              shipaEntities,
              socialMediaPlatforms,
              reasons,
              reasonsByDepartment,
            }) =>
              LoadCallLogFieldsValuesSuccess({
                communicationTypes,
                departments,
                interactionBy,
                shipaEntities,
                socialMediaPlatforms,
                reasons,
                reasonsByDepartment,
              })
          ),
          catchError(({ error }) => of(LoadCallLogFieldsValuesFail({ error })))
        )
      )
    )
  );

  loadBillingServiceTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadBillingServiceTypes),
      withLatestFrom(this.store.select(selectBillingServiceTypes)),
      filter(([_action, billingServiceTypes]) => billingServiceTypes === null),
      exhaustMap(() =>
        this.dataService.getBillingServiceTypes().pipe(
          map(({ dateFormats, serviceTypes, transactionDateStrategies }) =>
            LoadBillingServiceTypesSuccess({ dateFormats, serviceTypes, transactionDateStrategies })
          ),
          catchError(({ error }) => of(LoadBillingServiceTypesFail({ error })))
        )
      )
    )
  );

  loadVehicleTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadVehicleTypes),
      withLatestFrom(this.store.select(selectVehicleTypes)),
      filter(([_action, vehicleTypes]) => vehicleTypes === null),
      exhaustMap(() =>
        this.dataService.getVehicleTypes().pipe(
          map((vehicleTypes) => LoadVehicleTypesSuccess({ vehicleTypes })),
          catchError(({ error }) => of(LoadVehicleTypesFail({ error })))
        )
      )
    )
  );
}
